/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type {
  CareerRole_MerchandisingJobCategory as CareerRoleCategory,
  CareerRole_MerchandisingJobCategory as JobCategory,
  CareerRole_PathwayLevel as ProficiencyLevel,
} from '__generated__/graphql-types';

import { useParams } from 'js/lib';

import { Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import CondensedCareerRoleCard from 'bundles/browse-common/components/CondensedCareerRoleCard';
import { useDomainEntity } from 'bundles/browse-common/contexts/DomainEntityContext';
import type { KnownDomainSlugs } from 'bundles/browse-common/types/domain';
import { isKnownDomainSlug } from 'bundles/browse-common/utils/domain-utils';
import { toJobCategory } from 'bundles/browse-common/utils/toJobCategory';
import type { PageConfig_ComponentConfig_PageConfig_ExploreRolesComponent_Fragment as ExploreRolesComponentPageConfig } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import { useMediumIntentContext } from 'bundles/search-common/providers/MediumIntentProvider';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useCareerRolesByDomain from 'bundles/unified-common/hooks/useCareerRolesByDomain';
import useCareerRolesCollection, {
  normalizeCareerRolesCollection,
} from 'bundles/unified-common/hooks/useCareerRolesCollection';
import CareerAcademyModule from 'bundles/unified-common/modules/CareerAcademyModule';
import type { CareerRolesCollectionQueryQuery as CareerRolesCollectionQueryType } from 'bundles/unified-common/queries/__generated__/CareerRolesCollection';
import type { CareerRolesCollectionType } from 'bundles/unified-common/types/careerRoles';
import { generateCacheKey, getChipFilters, getDropdownMenuProps } from 'bundles/unified-common/utils/pathwaysFilters';

import _t from 'i18n!nls/browse-common';

const styles = {
  container: css`
    .rc-ProductCardCollectionPlaceholder .cds-ProductCard-grid {
      min-height: unset !important;
      max-height: 280px;
      overflow: hidden;
    }

    a.cds-button-secondary {
      /* Button span across width on mobile */
      ${breakpoints.down('xs')} {
        width: 100%;
      }
    }
  `,
  wrapper: css`
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;
  `,
  contentOffset: css`
    /* Manually adjust the heading margin-top since we use condensed cards */
    margin-top: calc(var(--cds-spacing-600));
    ${breakpoints.down('sm')} {
      margin-top: 0;
    }
  `,
};

const knownRolelessDomains = [
  'health',
  'math-and-logic',
  'language-learning',
  'personal-development',
  'physical-science-and-engineering',
  'social-sciences',
] as const satisfies KnownDomainSlugs[];

type Props = {
  pageConfigComponent: ExploreRolesComponentPageConfig;
};

function useLoadExploreRolesData() {
  const { domain } = useDomainEntity() ?? {};
  const isConsumerSerp = useIsConsumerSerp();

  const chipFilters = getChipFilters();
  const dropdownMenuProps = getDropdownMenuProps();
  const [currentSelectedDropdownValue, setSelectedDropdownValue] = React.useState<ProficiencyLevel>(
    dropdownMenuProps?.defaultValue
  );
  const [currentChipFilterIndex, setCurrentChipFilterIndex] = React.useState(0);

  const viewedCombinations = React.useRef<Map<string, CareerRolesCollectionType | undefined>>(new Map());
  const combinationKey = generateCacheKey(currentSelectedDropdownValue, currentChipFilterIndex);
  const cachedContent = viewedCombinations.current.get(combinationKey);
  const skipQuery = viewedCombinations.current.has(combinationKey);

  const addToLocalCache = (data: CareerRolesCollectionQueryType) => {
    if (
      data.PathwaySkillCareerRoles?.queryRolesByPathwayLevelAndCategory !== undefined &&
      !viewedCombinations.current.has(combinationKey)
    ) {
      const normalizedData = normalizeCareerRolesCollection(data);

      if (normalizedData?.entities) {
        normalizedData.entities = normalizedData.entities.filter((e) =>
          domain?.slug ? e.domain?.id === domain?.slug : true
        );
      }

      viewedCombinations.current.set(combinationKey, normalizedData);
    }
  };

  const jobCategory = (): JobCategory | undefined => {
    if (isKnownDomainSlug(domain?.slug)) {
      return toJobCategory(domain.slug);
    }
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return currentChipFilterIndex === 0 ? undefined : (chipFilters[currentChipFilterIndex].id as CareerRoleCategory);
  };

  const shouldUseDomainQuery = !!domain || isConsumerSerp;
  // skip this query if it's a domain page or consumer SERP
  const { data: fetchedContent, loading: isCollectionLoading } = useCareerRolesCollection({
    ssr: false,
    skip: skipQuery || shouldUseDomainQuery,
    level: currentSelectedDropdownValue,
    limit: domain ? 10 : 2,
    jobCategory: jobCategory(),
    onCompleted: (data) => {
      addToLocalCache(data);
    },
  });
  const { mediumIntentData } = useMediumIntentContext();
  const domainId = mediumIntentData?.matchedEntity.replace(/\s+/g, '-').toLowerCase();
  const { data: fetchedContentByDomain, loading: isCollectionLoadingByDomain } = useCareerRolesByDomain({
    ssr: false,
    skip: skipQuery || !shouldUseDomainQuery || (!domain?.slug && !isConsumerSerp),
    domainId: domain?.slug || domainId || '',
  });

  return {
    setSelectedDropdownValue,
    setCurrentChipFilterIndex,
    isCollectionLoading: !cachedContent && (shouldUseDomainQuery ? isCollectionLoadingByDomain : isCollectionLoading),
    collection: cachedContent || (shouldUseDomainQuery ? fetchedContentByDomain : fetchedContent),
    chipFilters,
    dropdownMenuProps,
    currentChipFilterIndex,
    currentSelectedDropdownValue,
    domain,
  };
}

const ExploreRolesRenderer = (props: Props) => {
  const {
    pageConfigComponent: { hideFilters = false },
  } = props;

  const { domainSlug } = useParams();

  const track = useTracker();

  const {
    setSelectedDropdownValue,
    setCurrentChipFilterIndex,
    isCollectionLoading,
    collection,
    chipFilters,
    dropdownMenuProps,
    currentChipFilterIndex,
    currentSelectedDropdownValue,
    domain,
  } = useLoadExploreRolesData();

  const handleFilterChange = (index: number) => {
    setCurrentChipFilterIndex(index);
  };

  const handleDropdownChange = (value: ProficiencyLevel) => {
    setSelectedDropdownValue(value);
  };

  const trackCtaClick = () => {
    track('click_button', {
      button: {
        name: 'career_academy_cta',
        linkName: 'explore_all_roles',
        linkType: 'internal',
        linkURL: '/career-academy',
      },
    });
  };

  const filterProps = hideFilters
    ? {}
    : {
        filters: chipFilters,
        dropdownMenu: dropdownMenuProps,
        currentFilterIndex: currentChipFilterIndex,
        currentDropdownValue: currentSelectedDropdownValue,
        onFilterChange: handleFilterChange,
        onDropdownMenuChange: handleDropdownChange,
      };

  if (domainSlug && knownRolelessDomains.includes(domainSlug)) {
    return null;
  }

  if (collection && (collection.entities?.length ?? 0) === 0) {
    return null;
  }

  const labels = domain
    ? {
        headline: _t('Explore roles'),
        description: _t('Gain the knowledge and skills you need to advance.'),
      }
    : {
        headline: _t('Explore roles'),
        description: _t('Grow in your career and get new skills with these premium courses on a 7-day free trial'),
      };

  return (
    <PageGridContainer component="section" css={styles.container}>
      <Grid item xs={12}>
        <div css={styles.wrapper}>
          <CareerAcademyModule
            {...labels}
            collection={collection}
            cta={{
              text: _t('View all'),
              target: '/career-academy',
              variant: 'secondary',
              onClick: trackCtaClick,
            }}
            ProductCardComponent={CondensedCareerRoleCard}
            layout="text3/image9"
            backgroundColor="grey"
            showRoundedCorners
            isPageGridContainerAppliedOnPage={true}
            showAllEntries
            isCollectionLoading={isCollectionLoading}
            numOfEntriesPerCollection={2}
            defaultMobileRows={2}
            overrideItemsGridConfig={{ lg: 6, md: 6, sm: 6, xs: 12 }}
            overrideContentOffsets={styles.contentOffset}
            {...filterProps}
          />
        </div>
      </Grid>
    </PageGridContainer>
  );
};

export default ExploreRolesRenderer;
